import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useAuthStore } from "@/stores/useAuthStore";
import { getChainConfig } from "@/config/contracts";
import { setOrgFlag } from "@/features/organizations/gettingStarted/orgChecklistStorage";
import {
  userSearchTerm,
  type CreateOrganizationInput,
  type InviteRole,
  type OrgImageKind,
  type UpdateOrganizationInput,
} from "@/lib/organizations";

/**
 * Every org key starts with "orgs" and ends with the viewer: RLS and the
 * RPCs answer differently per caller (codes for admins only, presence for
 * members only), so a cached answer must never cross accounts. Mutations
 * invalidate the whole "orgs" prefix — org screens are small and few.
 */
export const orgKeys = {
  all: ["orgs"] as const,
  bySlug: (slug: string | undefined, viewer: string | null) => ["orgs", "slug", slug, viewer] as const,
  byId: (id: string | undefined, viewer: string | null) => ["orgs", "id", id, viewer] as const,
  openQuests: (orgId: string | undefined, chainId: number, viewer: string | null) =>
    ["orgs", "open-quests", orgId, chainId, viewer] as const,
  mine: (viewer: string | null) => ["orgs", "mine", viewer] as const,
  forUser: (userId: string | undefined, viewer: string | null) => ["orgs", "for-user", userId, viewer] as const,
  members: (orgId: string | undefined, viewer: string | null) => ["orgs", "members", orgId, viewer] as const,
  presence: (orgId: string | undefined, viewer: string | null) => ["orgs", "presence", orgId, viewer] as const,
  invites: (orgId: string | undefined, viewer: string | null) => ["orgs", "invites", orgId, viewer] as const,
  requests: (orgId: string | undefined, viewer: string | null) => ["orgs", "requests", orgId, viewer] as const,
  myRequest: (orgId: string | undefined, viewer: string | null) => ["orgs", "my-request", orgId, viewer] as const,
  linkPreview: (code: string | undefined, viewer: string | null) => ["orgs", "link-preview", code, viewer] as const,
  pendingInvites: (viewer: string | null) => ["orgs", "pending-invites", viewer] as const,
  userSearch: (orgId: string | undefined, term: string | null, viewer: string | null) =>
    ["orgs", "user-search", orgId, term, viewer] as const,
  shareActivity: (viewer: string | null) => ["orgs", "share-activity", viewer] as const,
  admin: (search: string, viewer: string | null) => ["orgs", "admin", search, viewer] as const,
};

/** The signed-in user id that ends every per-user org key. */
export function useViewer(): string | null {
  return useAuthStore((s) => s.session?.userId ?? null);
}

function useInvalidateOrgs() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: orgKeys.all });
}

// ─── Reads ──────────────────────────────────────────────────────────────────
export function useOrganization(slug: string | undefined) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.bySlug(slug, viewer),
    queryFn: () => organizations.getBySlug(slug as string),
    enabled: !!slug,
  });
}

export function useOrganizationById(id: string | null | undefined) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.byId(id ?? undefined, viewer),
    queryFn: () => organizations.getById(id as string),
    enabled: !!id,
  });
}

/** The org's open quests on the active network (public, like the org page). */
export function useOrgOpenQuests(orgId: string | undefined) {
  const { organizations } = useServices();
  const viewer = useViewer();
  const chainId = getChainConfig().chainId;
  return useQuery({
    queryKey: orgKeys.openQuests(orgId, chainId, viewer),
    queryFn: () => organizations.listOpenQuests(orgId as string, chainId),
    enabled: !!orgId,
  });
}

export function useMyOrganizations() {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.mine(viewer),
    queryFn: () => organizations.listMine(viewer as string),
    enabled: !!viewer,
  });
}

/**
 * Move the caller's personal quest into an org (one way). The quest, its
 * listing family, the org's quest lists and the re-issued certificates all
 * change, so every one of those caches is dropped.
 */
export function useMoveQuestToOrganization() {
  const { organizations } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { questId: string; orgId: string }) => organizations.moveQuestToOrganization(v.questId, v.orgId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["quests"] });
      void queryClient.invalidateQueries({ queryKey: orgKeys.all });
      // certificateKeys.all — spelled out: useCertificates imports this module.
      void queryClient.invalidateQueries({ queryKey: ["certificates"] });
    },
  });
}

export function useUserOrganizations(userId: string | undefined) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.forUser(userId, viewer),
    queryFn: () => organizations.listForUser(userId as string),
    // organization_members is readable by signed-in users only.
    enabled: !!userId && !!viewer,
  });
}

export function useOrgMembers(orgId: string | undefined) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.members(orgId, viewer),
    queryFn: () => organizations.listMembers(orgId as string),
    enabled: !!orgId && !!viewer,
  });
}

/** Members only. Refreshes every minute; buckets are coarse on purpose. */
export function useOrgPresence(orgId: string | undefined, isMember: boolean) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.presence(orgId, viewer),
    queryFn: () => organizations.getMemberPresence(orgId as string),
    enabled: !!orgId && !!viewer && isMember,
    refetchInterval: 60_000,
  });
}

export function useOrgInvites(orgId: string | undefined, isMember: boolean) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.invites(orgId, viewer),
    queryFn: () => organizations.listInvites(orgId as string),
    enabled: !!orgId && !!viewer && isMember,
  });
}

/**
 * People to invite, for the Invites tab. Pass an already-debounced query; a
 * term too short for userSearchTerm doesn't fetch.
 */
export function useInvitableUsers(orgId: string | undefined, query: string) {
  const { organizations } = useServices();
  const viewer = useViewer();
  const term = userSearchTerm(query);
  return useQuery({
    queryKey: orgKeys.userSearch(orgId, term, viewer),
    queryFn: () => organizations.searchUsersToInvite(orgId as string, term as string),
    enabled: !!orgId && !!viewer && !!term,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

export function useOrgJoinRequests(orgId: string | undefined, isAdmin: boolean) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.requests(orgId, viewer),
    queryFn: () => organizations.listJoinRequests(orgId as string),
    enabled: !!orgId && !!viewer && isAdmin,
  });
}

export function useMyOrgJoinRequest(orgId: string | undefined) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.myRequest(orgId, viewer),
    queryFn: () => organizations.getMyJoinRequest(orgId as string, viewer as string),
    enabled: !!orgId && !!viewer,
  });
}

export function useOrgLinkPreview(code: string | undefined) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.linkPreview(code, viewer),
    queryFn: () => organizations.previewLinkInvite(code as string),
    // Logged out too (org_invite_preview is anon-callable since
    // 20261010100000): the person tapping a link from a group chat sees
    // which org it is before being asked to sign in.
    enabled: !!code,
  });
}

export function useMyPendingOrgInvites() {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.pendingInvites(viewer),
    queryFn: () => organizations.listMyPendingInvites(),
    enabled: !!viewer,
  });
}

export function useShareActivity() {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.shareActivity(viewer),
    queryFn: () => organizations.getShareActivity(),
    enabled: !!viewer,
  });
}

export function useAdminOrganizations(search: string) {
  const { organizations } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgKeys.admin(search, viewer),
    queryFn: () => organizations.adminList(search || null),
    enabled: !!viewer,
  });
}

// ─── Writes ─────────────────────────────────────────────────────────────────
/** One hook for every org mutation, so each invalidates the same prefix. */
export function useOrgMutations() {
  const { organizations } = useServices();
  const invalidate = useInvalidateOrgs();
  const viewer = useViewer();
  const opts = { onSuccess: () => { void invalidate(); } };
  // An invite also ticks the org checklist's "Invite someone" step — the
  // invite list never says who made an invite, so the flag is the record.
  const invited = (orgId: string) => {
    void invalidate();
    if (viewer) setOrgFlag(viewer, orgId, "invited");
  };

  return {
    create: useMutation({ mutationFn: (input: CreateOrganizationInput) => organizations.create(input), ...opts }),
    update: useMutation({
      mutationFn: (v: { orgId: string; input: UpdateOrganizationInput }) => organizations.update(v.orgId, v.input),
      ...opts,
    }),
    setImage: useMutation({
      mutationFn: (v: { orgId: string; kind: OrgImageKind; file: File }) => organizations.setImage(v.orgId, v.kind, v.file),
      ...opts,
    }),
    clearImage: useMutation({
      mutationFn: (v: { orgId: string; kind: OrgImageKind }) => organizations.clearImage(v.orgId, v.kind),
      ...opts,
    }),
    remove: useMutation({ mutationFn: (orgId: string) => organizations.remove(orgId), ...opts }),
    createLinkInvite: useMutation({
      mutationFn: (orgId: string) => organizations.createLinkInvite(orgId),
      onSuccess: (_r, orgId) => invited(orgId),
    }),
    createEmailInvite: useMutation({
      mutationFn: (v: { orgId: string; email: string; role: InviteRole }) =>
        organizations.createEmailInvite(v.orgId, v.email, v.role),
      onSuccess: (_r, v) => invited(v.orgId),
    }),
    createUserInvite: useMutation({
      mutationFn: (v: { orgId: string; userId: string; role: InviteRole }) =>
        organizations.createUserInvite(v.orgId, v.userId, v.role),
      onSuccess: (_r, v) => invited(v.orgId),
    }),
    revokeInvite: useMutation({ mutationFn: (inviteId: string) => organizations.revokeInvite(inviteId), ...opts }),
    acceptLinkInvite: useMutation({ mutationFn: (code: string) => organizations.acceptLinkInvite(code), ...opts }),
    acceptEmailInvite: useMutation({ mutationFn: (inviteId: string) => organizations.acceptEmailInvite(inviteId), ...opts }),
    requestToJoin: useMutation({
      mutationFn: (v: { orgId: string; message: string | null }) => organizations.requestToJoin(v.orgId, v.message),
      ...opts,
    }),
    cancelJoinRequest: useMutation({ mutationFn: (id: string) => organizations.cancelJoinRequest(id), ...opts }),
    decideJoinRequest: useMutation({
      mutationFn: (v: { requestId: string; approve: boolean }) => organizations.decideJoinRequest(v.requestId, v.approve),
      ...opts,
    }),
    setMemberRole: useMutation({
      mutationFn: (v: { orgId: string; userId: string; role: InviteRole }) =>
        organizations.setMemberRole(v.orgId, v.userId, v.role),
      ...opts,
    }),
    transferOwnership: useMutation({
      mutationFn: (v: { orgId: string; userId: string }) => organizations.transferOwnership(v.orgId, v.userId),
      ...opts,
    }),
    removeMember: useMutation({
      mutationFn: (v: { orgId: string; userId: string }) => organizations.removeMember(v.orgId, v.userId),
      ...opts,
    }),
    leave: useMutation({ mutationFn: (orgId: string) => organizations.leave(orgId), ...opts }),
    setShareActivity: useMutation({ mutationFn: (share: boolean) => organizations.setShareActivity(share), ...opts }),
    adminSetVerification: useMutation({
      mutationFn: (v: { orgId: string; verified: boolean; note: string | null }) =>
        organizations.adminSetVerification(v.orgId, v.verified, v.note),
      ...opts,
    }),
    adminRename: useMutation({
      mutationFn: (v: { orgId: string; name: string | null; slug: string | null }) =>
        organizations.adminRename(v.orgId, v.name, v.slug),
      ...opts,
    }),
  };
}
